import React, { MouseEvent } from 'react'
import { Platform } from 'react-native'

import { DescriptionModal } from '../modal/DescriptionModal/DescriptionModal'

import { Tile as TileContainer } from './style'
import { TileText } from './components/TileText/TileText'
import { TileImage } from './components/TileImage/TileImage'

type Props = {
  title: string
  link?: string
  image: string
  onPress?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  description?: string
  noPreferenceSelection?: boolean
}

const tileInteractiveStyle = {
  appearance: 'none' as const,
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  font: 'inherit',
  width: '100%',
  textDecoration: 'none' as const,
  color: 'inherit',
  display: 'block' as const,
}

/**
 * OB-506: when a `link` is supplied, render <a href> so the browser
 * handles copy-link, open-in-new-tab and middle-click. Plain left-click
 * is intercepted and the caller's onPress fires after preventDefault.
 *
 * When no `link` is supplied (No preference, location pickers), render
 * <button> so onPress fires directly — these are actions without a URL.
 */
export const Tile = ({
  image,
  link,
  title,
  onPress,
  description,
  noPreferenceSelection,
}: Props) => {
  const isWeb = Platform.OS === 'web'

  let imageUrl = null
  if (image) {
    imageUrl = image
    // OB-497: Image urls are already having a query param by default, check for that instead domain parts before appending.
    // example urls:
    // - https://optios-files.localhost/?id=87
    // - https://files.optios.net/?id=2365939
    if (typeof imageUrl === 'string' && imageUrl.includes('?id=')) {
      imageUrl += '&w=160&h=160&fit=crop'
    }

    if (typeof imageUrl === 'string' && imageUrl.startsWith('//')) {
      imageUrl = `https:${imageUrl}`
    }
  }

  if (noPreferenceSelection === false) {
    return null
  }

  const tileBody = (
    <>
      <TileImage imageUrl={imageUrl} title={title} />
      <TileText isWeb={isWeb} title={title} />
    </>
  )

  if (!link) {
    return (
      <TileContainer>
        <DescriptionModal description={description} link={link} title={title} />
        <button
          type="button"
          onClick={(e) => onPress?.(e)}
          style={tileInteractiveStyle}
        >
          {tileBody}
        </button>
      </TileContainer>
    )
  }

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey
    ) {
      return
    }
    e.preventDefault()
    if (onPress) {
      onPress(e)
    }
  }

  return (
    <TileContainer>
      <DescriptionModal description={description} link={link} title={title} />
      <a href={link} onClick={handleClick} style={tileInteractiveStyle}>
        {tileBody}
      </a>
    </TileContainer>
  )
}
